import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, PhoneIncoming, PhoneMissed, User } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { StatTile } from '@/components/ui/StatTile';
import { Badge } from '@/components/ui/Badge';
import { Loader } from '@/components/ui/Loader';
import { fetchExtensionStatus } from '@/services/api';
import type { ExtensionStatus } from '@/types';
import { showErrorToast } from '@/utils/toast';
import { useStore } from '@/store/useStore';

export function ExtensionStatusDashboard() {
  const [extensions, setExtensions] = useState<ExtensionStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const loadInFlight = useRef(false);
  const setAnalyticsExtensionSearch = useStore(
    (state) => state.setAnalyticsExtensionSearch
  );

  const viewExtensionStats = (extensionNumber: string) => {
    setAnalyticsExtensionSearch(extensionNumber);
    window.requestAnimationFrame(() => {
      document.getElementById('extension-call-activity')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const loadExtensionStatus = useCallback(async () => {
    if (loadInFlight.current) return;
    loadInFlight.current = true;
    try {
      const data = await fetchExtensionStatus();
      setExtensions(data);
    } catch (error: any) {
      console.error('Error loading extension status:', error);
      showErrorToast(
        error,
        'Failed to load extension status',
        'extension-status-load-error'
      );
    } finally {
      loadInFlight.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExtensionStatus();

    // Auto-refresh every 5 seconds if enabled
    let interval: ReturnType<typeof setInterval> | undefined;
    if (autoRefresh) {
      interval = setInterval(loadExtensionStatus, 5000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, loadExtensionStatus]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'idle':
        return <Badge variant="success">Idle</Badge>;
      case 'ringing':
        return <Badge variant="warning">Ringing</Badge>;
      case 'busy':
        return <Badge variant="danger">Busy</Badge>;
      default:
        return <Badge variant="default">Unavailable</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'idle':
        return <Phone size={16} className="text-primary-500" />;
      case 'ringing':
        return <PhoneIncoming size={16} className="text-yellow-500" />;
      case 'busy':
        return <PhoneMissed size={16} className="text-red-500" />;
      default:
        return <User size={16} className="text-gray-400" />;
    }
  };

  const statusCounts = {
    idle: extensions.filter((e) => e.status === 'idle').length,
    ringing: extensions.filter((e) => e.status === 'ringing').length,
    busy: extensions.filter((e) => e.status === 'busy').length,
    unavailable: extensions.filter((e) => e.status === 'unavailable').length,
  };

  if (loading) {
    return (
      <Card>
        <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6">
          Extension Status
        </h2>
        <Loader text="Loading extension status..." />
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Extension Status
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Real-time extension monitoring
          </p>
        </div>
        <button
          onClick={() => setAutoRefresh(!autoRefresh)}
          className={`px-3 py-1.5 text-xs font-medium rounded-full ${
            autoRefresh
              ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
              : 'bg-panel text-gray-600 dark:bg-gray-700 dark:text-gray-300'
          }`}
        >
          {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatTile size="md" surface="inset" label="Idle" dotClassName="bg-primary-500" value={statusCounts.idle} />
        <StatTile size="md" surface="inset" label="Ringing" dotClassName="bg-amber-500" value={statusCounts.ringing} />
        <StatTile size="md" surface="inset" label="Busy" dotClassName="bg-red-500" value={statusCounts.busy} />
        <StatTile size="md" surface="inset" label="Offline" dotClassName="bg-gray-400" value={statusCounts.unavailable} />
      </div>

      {/* Extension List */}
      {extensions.length > 0 ? (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {extensions.map((ext, index) => (
            <motion.div
              key={ext.ext_id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="flex items-center justify-between p-3 bg-panel dark:bg-gray-900/60 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center space-x-3">
                {getStatusIcon(ext.status)}
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {ext.display_name || ext.ext_name || `Extension ${ext.ext_num}`}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    <button
                      type="button"
                      onClick={() => viewExtensionStats(ext.ext_num)}
                      className="font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-sm"
                      title={`View call statistics for extension ${ext.ext_num}`}
                    >
                      Ext {ext.ext_num}
                    </button>{' '}
                    • ID: {ext.ext_id}
                  </p>
                  {ext.presence_label && (
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400">
                      Presence: {ext.presence_label}
                    </p>
                  )}
                </div>
              </div>
              {getStatusBadge(ext.status)}
            </motion.div>
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">
          No extensions found
        </p>
      )}
    </Card>
  );
}
